import {
  animate,
  state,
  style,
  transition,
  trigger,
} from "@angular/animations";
import { CommonModule } from "@angular/common";
import { Component, inject, OnInit, ViewChild, ElementRef } from "@angular/core";
import { AuthService } from "../../../../core/services/auth.service";
import { UsersService } from "../../../../core/services/users.service";
import { AuthUser } from "../../../../core/models/auth/auth.interface";
import { User } from "../../../../core/models/user.interface";
import { LoaderService } from "../../../../core/shared/services/loader.service";
import { TEST_TYPES } from "../../../../core/shared/constants";
import { TableObjectivesComponent } from "./table-objectives/table-objectives.component";
import { Router } from "@angular/router";
import { FormsModule } from "@angular/forms";
import { ObjectivesService } from "../../../../core/services/objectives.service";
import { Utils } from "../../../../core/shared/utils";
import { ConfigurationService } from "../../../../core/services/configuration.service";
import { environment } from "../../../../../environments/environment";
import { BaseComponent } from "../../../../core/shared/base.component";
import { WaitlistService } from "../../../../core/services/waitlist.service";
import { SafePipe } from "../../../../core/shared/pipes";
import { YouTubePlayerComponent } from "../../../../core/shared/components/youtube-player/youtube-player.component";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Objective } from "../../../../core/models/objective.model";


@Component({
  selector: "app-objectives",
  standalone: true,
  imports: [CommonModule, TableObjectivesComponent, FormsModule, SafePipe, YouTubePlayerComponent],
  templateUrl: "./objectives.component.html",
  styleUrl: "./objectives.component.scss",
  animations: [
    trigger("fade", [
      state("visible", style({ opacity: 1 })),
      state("hidden", style({ opacity: 0, display: "none" })),
      transition("visible => hidden", [animate("500ms ease-in")]),
      transition("hidden => visible", [animate("500ms ease-out")]),
    ]),
  ],
})
export class ObjectivesComponent extends BaseComponent implements OnInit {
  private authService: AuthService = inject(AuthService);
  private usersService: UsersService = inject(UsersService);

  authUser: AuthUser = this.authService.getAuthUser()!;
  user: User | null = null;
  subscription: any = null;
  private loader = inject(LoaderService);
  private objectivesService = inject(ObjectivesService);
  private configService = inject(ConfigurationService);
  private waitlistService = inject(WaitlistService);
  messageHide: boolean = false;
  dateToday: string = new Date().toLocaleDateString("es-ES", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
  testsCompleted: boolean = true; // Para propósitos de prueba, ponemos true
  prdCompleted: boolean = true; // Para propósitos de prueba, ponemos true

  // Variable para mostrar el banner ejecutivo
  showSalaryBanner: boolean = false;
  isHighSalaryUser: boolean = false;

  // Variables para el modal de lista de espera
  showWaitlistModal: boolean = false;
  waitlistForm = {
    nombre: '',
    apellido: '',
    telefono: '',
    correoElectronico: '',
    provincia: '',
    distrito: ''
  };
  provincias: string[] = [
    'Lima', 'Arequipa', 'Cusco', 'La Libertad', 'Piura', 'Lambayeque',
    'Ancash', 'Junín', 'Ica', 'Cajamarca', 'Puno', 'Loreto', 'Huánuco',
    'San Martín', 'Tacna', 'Ayacucho', 'Ucayali', 'Apurímac', 'Huancavelica',
    'Tumbes', 'Amazonas', 'Moquegua', 'Pasco', 'Madre de Dios'
  ];
  distritos: { [key: string]: string[] } = {
    'Lima': [
      'Lima', 'Ancón', 'Ate', 'Barranco', 'Breña', 'Carabayllo', 'Chaclacayo',
      'Chorrillos', 'Cieneguilla', 'Comas', 'El Agustino', 'Independencia',
      'Jesús María', 'La Molina', 'La Victoria', 'Lince', 'Los Olivos',
      'Lurigancho', 'Lurín', 'Magdalena del Mar', 'Miraflores', 'Pachacámac',
      'Pucusana', 'Pueblo Libre', 'Puente Piedra', 'Punta Hermosa',
      'Punta Negra', 'Rímac', 'San Bartolo', 'San Borja', 'San Isidro',
      'San Juan de Lurigancho', 'San Juan de Miraflores', 'San Luis',
      'San Martín de Porres', 'San Miguel', 'Santa Anita', 'Santa María del Mar',
      'Santa Rosa', 'Santiago de Surco', 'Surquillo', 'Villa El Salvador',
      'Villa María del Triunfo'
    ]
  };
  isSubmittingWaitlist: boolean = false;

  // ViewChild para el reproductor de YouTube
  @ViewChild('youtubePlayer', { static: false }) youtubePlayer!: ElementRef;

  @ViewChild(TableObjectivesComponent)
  tableComponent!: TableObjectivesComponent;

  constructor(private router: Router) {
    super();
  }

  // Método para obtener el tipo de plan según el salario del usuario
  getPlanType(): string {
    return this.isHighSalaryUser ? 'Executive' : 'Professional';
  }

  ngOnInit(): void {
    this.loadUserData();
  }

  async loadUserData() {
    // Para la maquetación con datos mock, no necesitamos cargar tests
    // simplemente simular que están completados
    this.messageHide = true; // Ocultamos el mensaje para pruebas
    this.showObjectivesVideo();

    // Verificar elegibilidad para el banner ejecutivo
    await this.getUserData();
  }

  // Verificar elegibilidad para Executive basado en rango salarial
  checkSuscriptionPublicityToShow() {
    console.log('checkSuscriptionPublicityToShow: ejecutándose...');
    console.log('checkSuscriptionPublicityToShow: user is ', this.user);

    if (!this.user) {
      console.log('checkSuscriptionPublicityToShow: No hay usuario');
      return;
    }

    if (this.user.type && this.user.type.id != 1) {
      console.log('checkSuscriptionPublicityToShow: Usuario no es Cliente');
      return;
    }

    if (this.user.type.suscriptionType && this.user.type.suscriptionType.id != "1") {
      console.log('checkSuscriptionPublicityToShow: Usuario ya esta suscrito ', this.user.type);
      return;
    }

    // Show the Suscribe banner (Professional by default)
    this.showSalaryBanner = true;

    const questionSalary = this.user.profile.registerQuestions.find(q =>
      q.title?.includes("¿En qué rango aproximado están tus ingresos  mensuales? Tu respuesta es confidencial y solo se usa para personalizar tu experiencia.")
    );

    if (questionSalary.value === "5001-más USD") {
      console.log('Ready for Executive');
      // Make the banner Executive
      this.isHighSalaryUser = true;
    }

  }

  hideMessage() {
    this.messageHide = true;
  }

  goToResults() {
    this.router.navigate(["/results"]);
  }

  async startDownloadPDF() {
    this.loader.show();
    await this.downloadPDF();
    this.loader.hide();
  }

  async downloadPDF() {
    try {
      const doc = new jsPDF({ orientation: 'landscape' });
      const margin = 14;
      let currentY = 20;

      const red: [number, number, number] = [200, 0, 0];
      const white: [number, number, number] = [255, 255, 255];

      // LOGO
      try {
        const response = await fetch("assets/img/phoenix-logo.png");
        const blob = await response.blob();
        const base64data = await this.blobToBase64(blob);
        doc.addImage(base64data, "PNG", margin, 10, 60, 10);
        currentY += 20;
      } catch { }

      const objectives: Objective[] = this.tableComponent.dataSource || [];

      for (const obj of objectives) {

        // === OBJECTIVE TABLE ===
        autoTable(doc, {
          startY: currentY,
          head: [['Objetivo', 'Dimensión', 'Inicio', 'Fin', 'Estado']],
          body: [[
            obj.name,
            this.wrapText(typeof obj.dimension === 'string' ? obj.dimension : obj.dimension.name, 50),
            obj.start,
            obj.end,
            obj.state
          ]],
          columnStyles: {
            //0: { cellWidth: 60 },
            1: { cellWidth: 50 },
            2: { cellWidth: 25 },
            3: { cellWidth: 25 },
            4: { cellWidth: 30 },
          },
          styles: {
            fontSize: 9,
            cellPadding: 4,
            lineWidth: 0.4,
            lineColor: red,
            fillColor: white,
          },
          headStyles: {
            fillColor: red,
            textColor: white,
            lineColor: red,
            lineWidth: 0.5,
            fontStyle: 'bold'
          },
          bodyStyles: {
            fillColor: white,
            textColor: [0, 0, 0],
          },
          margin: { left: margin, right: margin }
        });

        currentY = (doc as any).lastAutoTable.finalY + 4;

        // === ACTIVITIES TABLE ===
        if (obj.activities?.length) {
          autoTable(doc, {
            startY: currentY,
            head: [['Actividad', 'Descripción', 'Inicio', 'Fin', 'Estado']],
            body: obj.activities.map(act => [
              this.wrapText(act.name, 50),
              this.wrapText(act.description, 100),
              act.start,
              act.end,
              act.state
            ]),
            styles: {
              fontSize: 8,
              cellPadding: 3,
              lineWidth: 0.4,
              lineColor: red,
              fillColor: white,
            },
            headStyles: {
              fillColor: red,
              textColor: white,
              lineColor: red,
              lineWidth: 0.5,
              fontStyle: 'bold'
            },
            bodyStyles: {
              fillColor: white,
              textColor: [0, 0, 0]
            },
            margin: { left: margin + 6, right: margin }
          });

          currentY = (doc as any).lastAutoTable.finalY + 12;
        }

        if (currentY > doc.internal.pageSize.height - 30) {
          doc.addPage();
          currentY = 20;
        }
      }

      const now = new Date();
      const fileName = `Reporte_Objetivos_${now.toISOString().split('.')[0].replace(/[:T]/g, '-')}.pdf`;
      doc.save(fileName);

    } catch (error) {
      console.error(error);
      alert('Error al generar el reporte.');
    }
  }


  // === TEXT WRAPPER ===
  private wrapText(text: string, maxLength: number = 15): string {
    if (!text) return '';
    return text.match(new RegExp(`.{1,${maxLength}}`, 'g'))?.join('\n') || text;
  }


  createObjective() {
    this.router.navigate(["dashboard/new-objective"], {
      state: { objetivo: null, editable: true },
    });
  }

  toPRD() {
    this.router.navigate(["dashboard/prd"]);
  }

  navigateToMentors() {
    this.router.navigate(["dashboard/mentors"]);
  }

  showObjectivesVideo(): void {
    this.loader.show();
    this.configService.getEmbeddedVideos().subscribe({
      next: (response: any) => {
        this.loader.hide();
        if (response?.data && response.data.length > 0) {
          const videoData = response.data.find(
            (video: any) => video.type === "video_modal_objectives"
          );

          if (videoData && videoData.url) {
            // Obtener el ID del video
            const videoId = Utils.extractYoutubeVideoId(videoData.url);
            if (videoId) {
              console.log(videoId);
              //this.showVideoModal(videoId);
            }
          }
        }
      },
      error: (error: any) => {
        console.error("Error al obtener videos embebidos:", error);
        this.loader.hide();
      },
    });
  }

  /**
   * Muestra el modal con el video y controles de sonido
   */
  showVideoModal(videoId: string): void {
    // Crear el HTML del video con el botón de sonido
    const videoHtml = `<lite-youtube 
    videoId="${videoId}"
    ></lite-youtube>`;

    // Mostrar el modal con video
    this.info(
      videoHtml,
      "Crea tus objetivos",
      "Empezar ahora",
      false
    );
  }

  // Verificar elegibilidad para Executive - mostrar para usuarios freemium
  async getUserData() {
    // Obtener información del usuario desde el backend
    if (!this.authUser || !this.authUser.id) {
      console.log('No hay usuario autenticado');
      return;
    }

    console.log('Obteniendo datos del usuario desde el backend...');

    this.usersService.getUserWithSubscription(this.authUser.id).subscribe({
      next: (response: any) => {
        if (response && response.data) {
          this.user = response.data;
          this.subscription = response.data.subscription;
          console.log('Datos del usuario obtenidos:', this.user);
          console.log('Datos de suscripción obtenidos:', this.subscription);
          this.checkSuscriptionPublicityToShow();
        }
      },
      error: (error) => {
        console.error('Error al obtener datos del usuario:', error);
      }
    });
  }

  // Cerrar banner ejecutivo
  closeExecutiveBanner() {
    this.showSalaryBanner = false;
  }

  // Agendar entrevista desde banner
  scheduleInterviewFromBanner() {
    const planType = this.getPlanType();

    if (planType === 'Executive') {
      // Usuario con salario alto, redirigir a WhatsApp
      let userName = this.user?.firstName || this.user?.name || 'Usuario';
      const message = environment.whatsapp.executiveMessage.replace('{userName}', userName);
      const phoneNumber = environment.whatsapp.phoneNumber;
      const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;

      // Abrir WhatsApp en nueva ventana
      window.open(whatsappUrl, '_blank');
    } else {
      // Usuario Professional, abrir modal de lista de espera
      this.openWaitlistModal();
    }
  }

  // Abrir modal de lista de espera
  openWaitlistModal() {
    // Pre-llenar algunos campos con información del usuario si está disponible
    if (this.user) {
      this.waitlistForm.nombre = this.user.firstName || '';
      this.waitlistForm.apellido = this.user.lastName || '';
      this.waitlistForm.correoElectronico = this.user.email || '';
      this.waitlistForm.telefono = this.user.phone || '';
    }
    this.showWaitlistModal = true;
  }

  // Cerrar modal de lista de espera
  closeWaitlistModal() {
    this.showWaitlistModal = false;
    this.resetWaitlistForm();
  }

  // Resetear formulario de lista de espera
  resetWaitlistForm() {
    this.waitlistForm = {
      nombre: '',
      apellido: '',
      telefono: '',
      correoElectronico: '',
      provincia: '',
      distrito: ''
    };
  }

  // Obtener distritos por provincia
  getDistritosByProvincia(): string[] {
    if (this.waitlistForm.provincia && this.distritos[this.waitlistForm.provincia]) {
      return this.distritos[this.waitlistForm.provincia];
    }
    return [];
  }

  // Limpiar distrito cuando cambia la provincia
  onProvinciaChange() {
    this.waitlistForm.distrito = '';
  }

  // Validar formulario de lista de espera
  isWaitlistFormValid(): boolean {
    return !!(
      this.waitlistForm.nombre?.trim() &&
      this.waitlistForm.apellido?.trim() &&
      this.waitlistForm.telefono?.trim() &&
      this.waitlistForm.correoElectronico?.trim() &&
      this.waitlistForm.provincia?.trim() &&
      this.waitlistForm.distrito?.trim()
    );
  }

  // Enviar formulario de lista de espera
  async submitWaitlistForm() {
    if (!this.isWaitlistFormValid()) {
      this.alert('Por favor, completa todos los campos requeridos.');
      return;
    }

    this.isSubmittingWaitlist = true;
    this.loader.show();

    try {
      // Preparar datos para enviar
      const waitlistData = {
        ...this.waitlistForm,
        userId: this.authUser?.id || undefined,
        createdAt: new Date().toISOString()
      };

      // Llamar al servicio de waitlist
      await this.waitlistService.registerToWaitlist(waitlistData).toPromise();

      this.closeWaitlistModal();
      this.alert('¡Gracias! Te has registrado exitosamente en nuestra lista de espera. Te contactaremos pronto.');

    } catch (error) {
      console.error('Error al registrar en lista de espera:', error);
      this.alert('Hubo un error al registrarte. Por favor, inténtalo de nuevo.');
    } finally {
      this.isSubmittingWaitlist = false;
      this.loader.hide();
    }
  }



  // Obtener URL del video de Phoenix Professional
  getProfessionalVideoUrl(): string {
    return environment.urlVideoProfessional;
  }

  // Obtener ID del video de Phoenix Professional para el reproductor de YouTube
  getProfessionalVideoId(): string {
    return YouTubePlayerComponent.extractVideoId(this.getProfessionalVideoUrl()) || '';
  }

  // Obtener parámetros del video para el reproductor de YouTube
  getVideoParams(): string {
    return 'autoplay=0&mute=0&enablejsapi=1&origin=' + window.location.origin + '&rel=0&showinfo=0';
  }

  omitVideo() { }

  /**
   * Convertir blob a base64
   */
  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(blob);
    });
  }

}
